import { useState } from 'react';
import type { Movie, Trailer } from '@filmsloth/shared';
import { useMovieTitle, useT } from '../i18n/index.js';
import { coverGradient } from '../lib/cover.js';
import { useTrailerConsent } from '../lib/useTrailerConsent.js';
import { TrailerReportButton } from './TrailerReportButton.js';

/**
 * The trailer, shown through YouTube's own player and never loaded before the
 * viewer asks for it.
 *
 * Nothing is requested from YouTube until the first play — not the iframe and
 * not the thumbnail, which would already hand over the viewer's IP. Before
 * that the card's generated cover doubles as the preview surface. Once
 * somebody has agreed, later cards may show the real thumbnail, still behind a
 * click so no video starts on its own.
 */
export function TrailerPlayer({ movie, trailer }: { movie: Movie; trailer: Trailer }) {
  const t = useT();
  const movieTitle = useMovieTitle();
  const { granted, grant, revoke } = useTrailerConsent();
  const [playing, setPlaying] = useState(false);

  const start = () => {
    if (!granted) grant();
    setPlaying(true);
  };

  if (playing) {
    return (
      <div className="trailer">
        <iframe
          className="trailer__frame"
          // The privacy-enhanced host, and `rel=0` so the player does not end
          // on a wall of unrelated recommendations.
          src={`https://www.youtube-nocookie.com/embed/${trailer.videoId}?autoplay=1&rel=0`}
          title={t('trailer.label', { title: movieTitle(movie) })}
          allow="autoplay; encrypted-media; picture-in-picture; fullscreen"
          allowFullScreen
        />
      </div>
    );
  }

  return (
    <div className="trailer">
      <button type="button" className="trailer__preview" onClick={start}>
        {granted ? (
          <img
            className="trailer__thumb"
            src={`https://i.ytimg.com/vi/${trailer.videoId}/hqdefault.jpg`}
            alt=""
            loading="lazy"
          />
        ) : (
          <span className="trailer__cover" style={{ background: coverGradient(movie.id) }} />
        )}
        <span className="trailer__play" aria-hidden="true">
          ▶
        </span>
        <span className="trailer__label">{t('trailer.play')}</span>
      </button>

      {/* Where the entry came from is part of judging it, so it stays visible. */}
      {trailer.source === 'community' && (
        <p className="trailer__source small muted">{t('trailer.community')}</p>
      )}

      {/* Nothing automatic can tell a spoiler-heavy or wrong-language cut from
          the right one, so the people watching get to say. */}
      <TrailerReportButton movie={movie} />

      {granted ? (
        <button type="button" className="btn btn--ghost small trailer__off" onClick={revoke}>
          {t('trailer.disable')}
        </button>
      ) : (
        <p className="trailer__note small muted">{t('trailer.consentNote')}</p>
      )}
    </div>
  );
}
